import { useState, useEffect, useCallback, useMemo } from 'react'
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'
import { useCustomRangeDashboardLayoutStore } from '../stores/customRangeDashboardLayoutStore'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import {
    Chart as ChartJS,
    CategoryScale, LinearScale,
    BarElement, BarController,
    LineElement, LineController, PointElement,
    ArcElement,
    Tooltip, Legend, Filler
} from 'chart.js'
import { Navigate, useSearchParams } from 'react-router-dom'
import { api, API_BASE } from '../lib/api'
import { fmt, KPI_ACCENTS, KPI_NO_COMPARISONS } from '../lib/dashboardHelpers'
import { storeSelectionLabel, type EmployeePerformanceQueryContext } from '../lib/employeePerformance'
import type { KpiComparison, DashboardData, StoreConfig, DebugQuery } from '../types/dashboard'
import {
    KpiCard, HourlyChart, PaymentDonut, ChannelMixBar,
    TopSellersPanel, SoldOutPanel, SqlDebugModal, MobileFooter
} from '../components/dashboard'
import MonthlyWidgetGrid from '../components/dashboard/MonthlyWidgetGrid'
import DateRangePicker, { buildPresets } from '../components/dashboard/DateRangePicker'
import PerformanceChart from '../components/dashboard/PerformanceChart'
import SalesByStoreChart from '../components/dashboard/SalesByStoreChart'
import EmployeePerformancePanel from '../components/dashboard/EmployeePerformancePanel'
import { useEmployeePerformance } from '../components/dashboard/useEmployeePerformance'
import WidgetGallery from '../components/WidgetGallery'
import LayoutPresetsMenu from '../components/dashboard/LayoutPresetsMenu'
import { useLayoutManager } from '../components/dashboard/useLayoutManager'
import '../styles/dashboard.css'
import '../styles/widget-gallery.css'

ChartJS.register(CategoryScale, LinearScale, BarElement, BarController, LineElement, LineController, PointElement, ArcElement, Tooltip, Legend, Filler)

// ── Season helpers (mirrors DateRangePicker) ─────────────────────────

function getSeasons() {
    const now = new Date()
    const y = now.getFullYear()
    const m = now.getMonth()

    const isLeap = (yr: number) => (yr % 4 === 0 && yr % 100 !== 0) || yr % 400 === 0

    if (m >= 2 && m <= 7) {
        // SS season
        const yy = String(y).slice(2)
        const prevFwYear = y - 1
        const prevFwYY = String(prevFwYear).slice(2).padStart(2, '0')
        const fwEndYear = prevFwYear + 1
        const fwEndDay = isLeap(fwEndYear) ? 29 : 28
        // preceding season of SS is FW-(y-1).  season before that is SS-(y-1)
        return {
            currentLabel: `SS${yy}`,
            currentStart: `${y}-03-01`, currentEnd: `${y}-08-31`,
            precedingLabel: `FW${prevFwYY}`,
            precedingStart: `${prevFwYear}-09-01`, precedingEnd: `${fwEndYear}-02-${fwEndDay}`,
            // "last year" for current season
            lastYearStart: `${y - 1}-03-01`, lastYearEnd: `${y - 1}-08-31`,
            // "last season" for preceding season
            precedingLastYearStart: `${prevFwYear - 1}-09-01`,
            precedingLastYearEnd: `${prevFwYear}-02-${isLeap(prevFwYear) ? 29 : 28}`,
        }
    } else {
        const fwYear = m >= 8 ? y : y - 1
        const fwYY = String(fwYear).slice(2).padStart(2, '0')
        const fwEndYear = fwYear + 1
        const fwEndDay = isLeap(fwEndYear) ? 29 : 28
        const ssYY = String(fwYear).slice(2).padStart(2, '0')
        return {
            currentLabel: `FW${fwYY}`,
            currentStart: `${fwYear}-09-01`, currentEnd: `${fwEndYear}-02-${fwEndDay}`,
            precedingLabel: `SS${ssYY}`,
            precedingStart: `${fwYear}-03-01`, precedingEnd: `${fwYear}-08-31`,
            lastYearStart: `${fwYear - 1}-09-01`,
            lastYearEnd: `${fwYear}-02-${isLeap(fwYear) ? 29 : 28}`,
            precedingLastYearStart: `${fwYear - 1}-03-01`,
            precedingLastYearEnd: `${fwYear - 1}-08-31`,
        }
    }
}

// ── Comparison helpers ───────────────────────────────────────────────

interface CompareOption {
    id: string
    label: string
    getRange: (start: string, end: string) => { start: string; end: string }
}

function getCompareOptions(presetId: string | null, startDate: string, endDate: string): CompareOption[] {
    const seasons = getSeasons()
    const opts: CompareOption[] = [{ id: 'none', label: 'None', getRange: () => ({ start: '', end: '' }) }]

    if (presetId === 'currentSeason') {
        // Compare to: Last Season or Last Year
        opts.push({
            id: 'lastSeason',
            label: `Last Season (${seasons.precedingLabel})`,
            getRange: () => ({ start: seasons.precedingStart, end: seasons.precedingEnd })
        })
        opts.push({
            id: 'lastYear',
            label: `Last Year (${seasons.currentLabel.replace(/\d+/, (d: string) => String(Number(d) - 1))})`,
            getRange: () => ({ start: seasons.lastYearStart, end: seasons.lastYearEnd })
        })
    } else if (presetId === 'precedingSeason') {
        // Compare to: Last Season (the one before preceding) or Last Year
        opts.push({
            id: 'lastSeason',
            label: `Last Season (${seasons.currentLabel.replace(/\d+/, (d: string) => String(Number(d) - 1))})`,
            getRange: () => {
                // The season before preceding. E.g. if preceding = FW25, last season = SS25
                // If preceding = SS25, last season = FW24
                return { start: seasons.precedingLastYearStart, end: seasons.precedingLastYearEnd }
            }
        })
        opts.push({
            id: 'lastYear',
            label: `Last Year`,
            getRange: (start, end) => {
                const s = new Date(start + 'T12:00:00')
                const e = new Date(end + 'T12:00:00')
                s.setFullYear(s.getFullYear() - 1)
                e.setFullYear(e.getFullYear() - 1)
                return { start: fmtDate(s), end: fmtDate(e) }
            }
        })
    } else if (presetId === 'last30') {
        opts.push({
            id: 'priorPeriod',
            label: 'Prior Period (prev 30 days)',
            getRange: (start) => {
                const s = new Date(start + 'T12:00:00')
                const e = new Date(start + 'T12:00:00')
                e.setDate(e.getDate() - 1)
                s.setDate(s.getDate() - 30)
                return { start: fmtDate(s), end: fmtDate(e) }
            }
        })
        opts.push({
            id: 'lastYear',
            label: 'Last Year',
            getRange: (start, end) => {
                const s = new Date(start + 'T12:00:00')
                const e = new Date(end + 'T12:00:00')
                s.setFullYear(s.getFullYear() - 1)
                e.setFullYear(e.getFullYear() - 1)
                return { start: fmtDate(s), end: fmtDate(e) }
            }
        })
    } else if (presetId === 'last90') {
        opts.push({
            id: 'priorPeriod',
            label: 'Prior Period (prev 90 days)',
            getRange: (start) => {
                const s = new Date(start + 'T12:00:00')
                const e = new Date(start + 'T12:00:00')
                e.setDate(e.getDate() - 1)
                s.setDate(s.getDate() - 90)
                return { start: fmtDate(s), end: fmtDate(e) }
            }
        })
        opts.push({
            id: 'lastYear',
            label: 'Last Year',
            getRange: (start, end) => {
                const s = new Date(start + 'T12:00:00')
                const e = new Date(end + 'T12:00:00')
                s.setFullYear(s.getFullYear() - 1)
                e.setFullYear(e.getFullYear() - 1)
                return { start: fmtDate(s), end: fmtDate(e) }
            }
        })
    } else if (presetId === 'last365') {
        opts.push({
            id: 'lastYear',
            label: 'Last Year (prior 365 days)',
            getRange: (start) => {
                const s = new Date(start + 'T12:00:00')
                const e = new Date(start + 'T12:00:00')
                e.setDate(e.getDate() - 1)
                s.setDate(s.getDate() - 365)
                return { start: fmtDate(s), end: fmtDate(e) }
            }
        })
    } else if (presetId === 'lastCalYear') {
        opts.push({
            id: 'lastYear',
            label: 'Previous Calendar Year',
            getRange: (start, end) => {
                const s = new Date(start + 'T12:00:00')
                const e = new Date(end + 'T12:00:00')
                s.setFullYear(s.getFullYear() - 1)
                e.setFullYear(e.getFullYear() - 1)
                return { start: fmtDate(s), end: fmtDate(e) }
            }
        })
    } else {
        // Arbitrary range
        const s = new Date(startDate + 'T12:00:00')
        const e = new Date(endDate + 'T12:00:00')
        const days = Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24))

        opts.push({
            id: 'priorPeriod',
            label: 'Previous Period',
            getRange: (start, end) => {
                const sD = new Date(start + 'T12:00:00')
                const eD = new Date(end + 'T12:00:00')
                const span = Math.round((eD.getTime() - sD.getTime()) / (1000 * 60 * 60 * 24)) + 1
                const newEnd = new Date(sD)
                newEnd.setDate(newEnd.getDate() - 1)
                const newStart = new Date(newEnd)
                newStart.setDate(newStart.getDate() - span + 1)
                return { start: fmtDate(newStart), end: fmtDate(newEnd) }
            }
        })
        if (days < 365) {
            opts.push({
                id: 'lastYear',
                label: 'Previous Year',
                getRange: (start, end) => {
                    const sD = new Date(start + 'T12:00:00')
                    const eD = new Date(end + 'T12:00:00')
                    sD.setFullYear(sD.getFullYear() - 1)
                    eD.setFullYear(eD.getFullYear() - 1)
                    return { start: fmtDate(sD), end: fmtDate(eD) }
                }
            })
        }
    }

    // Always add Custom Range... at the end
    opts.push({
        id: 'custom',
        label: 'Custom Range…',
        getRange: () => ({ start: '', end: '' })
    })

    return opts
}

function fmtDate(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** "Aug 1 – Aug 31, 2026" — the start carries its own year only when the range crosses one. */
function fmtRangeLabel(start: string, end: string): string {
    const s = new Date(start + 'T12:00:00')
    const e = new Date(end + 'T12:00:00')
    if (isNaN(s.getTime()) || isNaN(e.getTime())) return `${start} – ${end}`
    const withYear: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
    if (start === end) return e.toLocaleDateString('en-US', withYear)
    const startOpts: Intl.DateTimeFormatOptions = s.getFullYear() === e.getFullYear() ? { month: 'short', day: 'numeric' } : withYear
    return `${s.toLocaleDateString('en-US', startOpts)} – ${e.toLocaleDateString('en-US', withYear)}`
}

// ── Default range (last 90 days) ─────────────────────────────────────

function defaultRange(): { start: string; end: string } {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 89)
    return { start: fmtDate(start), end: fmtDate(end) }
}

// ── Main Component ───────────────────────────────────────────────────

export default function CustomRangeDashboardPage() {
    const user = useAuthStore(s => s.user)
    const token = useAuthStore(s => s.accessToken)
    const qc = useQueryClient()

    const {
        layouts, activeWidgets, widgetSettings, isEditing, layoutVersion,
        currentLayoutId,
        compareFull, setCompareFull,
        setEditing, removeWidget, setLayouts, snapshotLayouts,
        revertToInitial, updateWidgetSettings,
        loadFromServer, loadLayout, saveAsSystemDefault,
        loaded, datePreset, compareMode: storeCompareMode, customCompareStart: storeCustomCompareStart,
        customCompareEnd: storeCustomCompareEnd, setDateSettings,
        applyImportedLayout,
    } = useCustomRangeDashboardLayoutStore()

    // Store config
    const { data: storeConfig, isLoading: configLoading } = useQuery<StoreConfig>({
        queryKey: ['dashboard-stores'],
        queryFn: () => api('/api/dashboard/stores', token),
        staleTime: 60_000
    })

    const [searchParams, setSearchParams] = useSearchParams()

    const defRange = useMemo(() => defaultRange(), [])
    const [storeId, setStoreId] = useState<string>(searchParams.get('storeId') || 'group')
    const [channel, setChannel] = useState<string>(searchParams.get('channel') || 'all')
    const [includeReturns, setIncludeReturns] = useState(searchParams.get('includeReturns') !== 'false')
    const [includeMultibuys, setIncludeMultibuys] = useState(searchParams.get('includeMultibuys') !== 'false')
    const [startDate, setStartDate] = useState(searchParams.get('startDate') || defRange.start)
    const [endDate, setEndDate] = useState(searchParams.get('endDate') || defRange.end)
    const [activePresetId, setActivePresetId] = useState<string | null>(searchParams.get('preset') || 'last90')

    const [compareMode, setCompareMode] = useState<string>(searchParams.get('compare') || 'none')
    const [customCompareStart, setCustomCompareStart] = useState(searchParams.get('compareStart') || '')
    const [customCompareEnd, setCustomCompareEnd] = useState(searchParams.get('compareEnd') || '')

    const [showDebug, setShowDebug] = useState(false)
    const [showGallery, setShowGallery] = useState(false)
    const [overwriteSystemDefault, setOverwriteSystemDefault] = useState(false)
    const [overwriteUserCustomizations, setOverwriteUserCustomizations] = useState(false)
    const [settingsOpen, setSettingsOpen] = useState<string | null>(null)
    const [actionMenuOpen, setActionMenuOpen] = useState(false)

    const isAdmin = user?.role === 'SuperAdmin' || user?.role === 'HeadOffice'

    // Sync store -> local state on layout/preset changes
    useEffect(() => {
        if (!loaded) return
        
        if (datePreset) {
            const presets = buildPresets()
            const p = presets.find(x => x.id === datePreset)
            if (p) {
                const range = p.getRange()
                setStartDate(range.start)
                setEndDate(range.end)
                setActivePresetId(datePreset)
            }
        } else {
            setActivePresetId(null)
        }

        setCompareMode(storeCompareMode || 'none')
        setCustomCompareStart(storeCustomCompareStart || '')
        setCustomCompareEnd(storeCustomCompareEnd || '')
        
    }, [loaded, datePreset, storeCompareMode, storeCustomCompareStart, storeCustomCompareEnd])

    // Comparison options based on preset/range
    const compareOptions = useMemo(
        () => getCompareOptions(activePresetId, startDate, endDate),
        [activePresetId, startDate, endDate]
    )

    // Compute actual compare dates
    const computedCompare = useMemo(() => {
        if (compareMode === 'none') return { start: '', end: '' }
        if (compareMode === 'custom') return { start: customCompareStart, end: customCompareEnd }
        const opt = compareOptions.find(o => o.id === compareMode)
        if (!opt) return { start: '', end: '' }
        return opt.getRange(startDate, endDate)
    }, [compareMode, compareOptions, startDate, endDate, customCompareStart, customCompareEnd])

    // Reset compare mode when it's no longer available
    useEffect(() => {
        if (compareMode !== 'none' && compareMode !== 'custom') {
            const still = compareOptions.find(o => o.id === compareMode)
            if (!still) setCompareMode('none')
        }
    }, [compareOptions, compareMode])

    // Set defaults from user preferences once loaded
    useEffect(() => {
        if (storeConfig) {
            const initialParams = new URLSearchParams(window.location.search)
            if (!initialParams.has('storeId')) setStoreId(storeConfig.defaultView)
            if (!initialParams.has('channel')) setChannel(storeConfig.defaultChannel)
        }
    }, [storeConfig])

    // Load layout from server
    useEffect(() => {
        if (token && user) {
            const requestedLayoutId = new URLSearchParams(window.location.search).get('layout')
            if (requestedLayoutId) {
                // loadLayout doesn't fetch the presets list; loadFromServer then fills it in
                // (and skips the layout itself, which is loaded by then)
                loadLayout(token, user.id, 'custom-range', requestedLayoutId).then(() => loadFromServer(token, user.id))
            } else {
                loadFromServer(token, user.id)
            }
        }
    }, [token, user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

    // Sync state to URL
    useEffect(() => {
        const params = new URLSearchParams()
        if (currentLayoutId) params.set('layout', currentLayoutId)
        if (storeId !== 'group') params.set('storeId', storeId)
        if (channel !== 'all') params.set('channel', channel)
        if (!includeReturns) params.set('includeReturns', 'false')
        if (!includeMultibuys) params.set('includeMultibuys', 'false')
        params.set('startDate', startDate)
        params.set('endDate', endDate)
        if (activePresetId) params.set('preset', activePresetId)
        if (compareMode && compareMode !== 'none') {
            params.set('compare', compareMode)
            if (compareMode === 'custom' && customCompareStart && customCompareEnd) {
                params.set('compareStart', customCompareStart)
                params.set('compareEnd', customCompareEnd)
            }
        }

        setSearchParams(params, { replace: true })
    }, [currentLayoutId, storeId, channel, includeReturns, includeMultibuys, startDate, endDate, activePresetId, compareMode, customCompareStart, customCompareEnd, setSearchParams])

    // Save preference
    const savePreference = useCallback((view: string, ch: string) => {
        fetch(API_BASE + '/api/user/preferences', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({ dashboardDefaultView: view, dashboardDefaultChannel: ch })
        })
    }, [token])

    // Build API URL
    const apiUrl = useMemo(() => {
        let url = `/api/dashboard/custom-range?storeId=${storeId}&channel=${channel}&includeReturns=${includeReturns}&includeMultibuys=${includeMultibuys}&compareFull=${compareFull}&startDate=${startDate}&endDate=${endDate}`
        if (computedCompare.start && computedCompare.end) {
            url += `&compareStartDate=${computedCompare.start}&compareEndDate=${computedCompare.end}`
        }
        // Tell backend which widgets are visible so it can skip expensive queries for hidden ones
        if (activeWidgets.length > 0) {
            url += `&widgets=${activeWidgets.join(',')}`
        }
        return url
    }, [storeId, channel, includeReturns, includeMultibuys, compareFull, startDate, endDate, computedCompare, activeWidgets])

    // Dashboard data
    const { data, isLoading, isFetching, error, dataUpdatedAt } = useQuery<DashboardData>({
        queryKey: ['dashboard-custom-range', apiUrl],
        queryFn: () => api(apiUrl, token),
        enabled: !!storeConfig,
        placeholderData: keepPreviousData,
        retry: false
    })

    const hasSalesByStoreWidget = activeWidgets.includes('salesByStore')
    const { data: salesByStoreData, isFetching: salesByStoreFetching } = useQuery<any[]>({
        queryKey: ['dashboard-sales-by-store-custom', storeId, channel, includeReturns, includeMultibuys, startDate, endDate],
        queryFn: () => api(`/api/dashboard/sales-by-store?period=custom&storeId=${storeId}&channel=${channel}&includeReturns=${includeReturns}&includeMultibuys=${includeMultibuys}&startDate=${startDate}&endDate=${endDate}`, token),
        enabled: !!storeConfig && hasSalesByStoreWidget && !!startDate && !!endDate,
        placeholderData: keepPreviousData,
        retry: false
    })

    // Keyed under 'dashboard-custom-range' so Refresh below refetches it along with the main query
    const hasEmployeePerformance = activeWidgets.includes('employeePerformance')
    // The same context goes to the widget, whose drill-down asks with exactly these filters.
    const employeePerformanceQuery: EmployeePerformanceQueryContext = {
        period: 'custom',
        filters: { storeId, channel, includeReturns, includeMultibuys, startDate, endDate },
        queryKeyPrefix: 'dashboard-custom-range',
    }
    const { data: employeePerformanceData, isPending: employeePerformancePending, isFetching: employeePerformanceFetching, isPlaceholderData: employeePerformanceStale, error: employeePerformanceError } = useEmployeePerformance({
        ...employeePerformanceQuery,
        enabled: !!storeConfig && hasEmployeePerformance && !!startDate && !!endDate,
        settings: widgetSettings['employeePerformance'],
    })

    const refresh = () => qc.invalidateQueries({ queryKey: ['dashboard-custom-range'] })

    // The API sends the SQL diagnostics to HeadOffice+ only (DashboardDiagnostics) — show
    // Debug SQL and the KPI cards' admin queries exactly when they arrive.
    const hasDiagnostics = data?.debug != null

    const hour = new Date().getHours()
    const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'

    const mobileDateLabel = useMemo(() => {
        if (activePresetId) {
            const presets = buildPresets()
            const p = presets.find(p => p.id === activePresetId)
            if (p) return `${p.label} (${startDate} → ${endDate})`
        }
        return `${startDate} → ${endDate}`
    }, [activePresetId, startDate, endDate])

    // ── Date range apply handler ──────────────────────────────────────
    const handleRangeApply = (start: string, end: string, presetId: string | null) => {
        setStartDate(start)
        setEndDate(end)
        setActivePresetId(presetId)
        
        // Calculate options for the new date range
        const newOptions = getCompareOptions(presetId, start, end)
        // Check if the current compareMode is valid for the new range
        const nextCompareMode = newOptions.some(o => o.id === compareMode) ? compareMode : 'none'
        
        setCompareMode(nextCompareMode)
        setDateSettings(presetId, nextCompareMode, customCompareStart, customCompareEnd)
    }

    // ── Save handlers ────────────────────────────────────────────────

    const handleCancel = () => revertToInitial()

    const handleSaveSystemDefault = async (close: () => void) => {
        close()
        setEditing(false)
        try {
            if (token) await saveAsSystemDefault(token, 'custom-range', overwriteUserCustomizations)
        } catch (e) {
            console.error('[dashboard] handleSaveSystemDefault FAILED:', e)
        }
    }

    // 💾 Save, Save as…, Revert and the Layouts menu — shared with the other dashboards
    const layoutUi = useLayoutManager(useCustomRangeDashboardLayoutStore, 'custom-range', {
        onOpenSaveDialog: () => {
            setOverwriteSystemDefault(false)
            setOverwriteUserCustomizations(false)
        },
        renderAdmin: isAdmin ? close => (
            <>
                <div className="admin-save-divider" />
                <h4 style={{ marginBottom: '0.5rem' }}>Admin Options</h4>
                <label className="admin-save-checkbox">
                    <input type="checkbox" checked={overwriteSystemDefault}
                        onChange={e => {
                            setOverwriteSystemDefault(e.target.checked)
                            if (!e.target.checked) setOverwriteUserCustomizations(false)
                        }} />
                    Save as System Default
                </label>
                <label className="admin-save-checkbox" style={{ marginTop: '8px' }}>
                    <input type="checkbox" checked={overwriteUserCustomizations}
                        onChange={e => {
                            setOverwriteUserCustomizations(e.target.checked)
                            if (e.target.checked) setOverwriteSystemDefault(true)
                        }} disabled={!overwriteSystemDefault} />
                    Overwrite existing User-Level Customizations
                </label>
                <div className="admin-save-actions" style={{ marginTop: 'var(--space-4)' }}>
                    <button className="btn btn-secondary" onClick={() => handleSaveSystemDefault(close)}
                        disabled={!overwriteSystemDefault}
                        style={!overwriteSystemDefault ? { opacity: 0.4, cursor: 'not-allowed' } : { borderColor: 'var(--color-error)', color: 'var(--color-error)' }}>
                        Execute Admin Save
                    </button>
                </div>
            </>
        ) : undefined,
    })

    const handleExportLayout = () => {
        const store = useCustomRangeDashboardLayoutStore.getState()
        return JSON.stringify({
            layouts, activeWidgets, widgetSettings,
            primaryComparison: store.primaryComparison,
            datePreset: store.datePreset,
            compareMode: store.compareMode,
            customCompareStart: store.customCompareStart,
            customCompareEnd: store.customCompareEnd,
        }, null, 2)
    }

    const handleImportLayout = (json: string): string | null => {
        let parsed: any
        try {
            parsed = JSON.parse(json)
        } catch {
            return 'Invalid JSON syntax. Please check the format and try again.'
        }

        if (!parsed.activeWidgets || !Array.isArray(parsed.activeWidgets)) {
            return 'Invalid layout: missing "activeWidgets" array.'
        }
        if (!parsed.layouts || typeof parsed.layouts !== 'object') {
            return 'Invalid layout: missing "layouts" object.'
        }

        applyImportedLayout(parsed)
        return null
    }

    // ── Widget renderer ──────────────────────────────────────────────

    const renderWidget = (widgetId: string) => {
        if (!data?.success) return null
        const settings = widgetSettings[widgetId] ?? { size: 'full', showLastMonth: true, showLastYear: true }

        const kpiQueries = data.debug?.filter(q => q.label.startsWith('KPI')) ?? []
        const invQueries = data.debug?.filter(q => q.label === 'Inventory Metrics') ?? []

        const hasCompare = compareMode !== 'none' && computedCompare.start !== ''

        // Derive a human-readable comparison label
        let compLabel = 'prior'
        if (hasCompare) {
            if (compareMode === 'lastYear') compLabel = 'prior year'
            else if (compareMode === 'lastSeason') {
                const opt = compareOptions.find(o => o.id === 'lastSeason')
                compLabel = opt ? opt.label.toLowerCase() : 'last season'
            }
            else if (compareMode === 'priorPeriod') compLabel = 'prior'
            else if (compareMode === 'custom') compLabel = 'compare period'
            else {
                const opt = compareOptions.find(o => o.id === compareMode)
                if (opt) compLabel = opt.label.toLowerCase()
            }
        }

        const kpiMap: Record<string, { label: string; kpi: KpiComparison; format?: 'currency' | 'percent' | 'int' | 'decimal'; lowerIsBetter?: boolean; description: string; queries?: DebugQuery[] }> = {
            netSales: { label: includeReturns ? 'Net Sales' : 'Gross Sales', kpi: data.kpis.netSales, format: 'currency', description: "Total revenue calculated using the transaction subtotal (excluding taxes).\n\nNet Sales includes product returns if the 'Net (with returns)' toggle is enabled.", queries: kpiQueries },
            margin: {
                label: 'Margin', kpi: {
                    today: data.kpis.netSales.today > 0 ? data.kpis.margin.today / data.kpis.netSales.today : 0,
                    lastWeek: 0, yesterday: 0,
                    lastMonth: data.kpis.netSales.lastMonth && data.kpis.netSales.lastMonth > 0 ? data.kpis.margin.lastMonth! / data.kpis.netSales.lastMonth : 0,
                    lastYear: 0,
                }, format: 'percent', description: "Gross margin as a percentage of Net Sales.", queries: kpiQueries
            },
            costOfSales: { label: 'Cost of Sales', kpi: data.kpis.costOfSales, format: 'currency', description: "Total cost of goods sold.", queries: kpiQueries },
            transactions: { label: `Transactions${channel === 'instore' ? ' - In-Store' : channel === 'online' ? ' - Online' : ' - All Channels'}`, kpi: data.kpis.transactions, description: "Total number of completed transactions.", queries: kpiQueries },
            unitsSold: { label: 'Units Sold', kpi: data.kpis.unitsSold, description: "Total number of items sold.", queries: kpiQueries },
            avgTransaction: { label: 'Avg Transaction', kpi: data.kpis.avgTransaction, format: 'currency', description: "Average revenue per transaction.", queries: kpiQueries },
            upt: { label: 'Units Per Txn', kpi: data.kpis.upt, format: 'decimal', description: "Units Per Transaction.", queries: kpiQueries },
            discountDepth: { label: 'Discount Depth', kpi: data.kpis.discountDepth, format: 'percent', lowerIsBetter: true, description: "Discount as a percentage of retail value.", queries: kpiQueries },
            sellThrough: { label: 'Sell-Through', kpi: data.kpis.sellThrough, format: 'percent', description: "Percentage of available inventory sold.", queries: invQueries },
            inventoryValue: { label: 'Inv Value on Hand', kpi: data.kpis.inventoryValue, format: 'currency', description: "Total cost value of on-hand inventory.", queries: invQueries },
            gmroi: { label: 'Est. GMROI (Ann.)', kpi: data.kpis.gmroi, format: 'percent', description: "Gross Margin Return on Investment (annualized).", queries: invQueries },
        }

        if (kpiMap[widgetId]) {
            const { label, kpi, format, lowerIsBetter, description, queries } = kpiMap[widgetId]
            const fmtStyle = format ?? 'int'
            const value = fmt(kpi.today, fmtStyle)

            return (
                <KpiCard
                    label={label} value={value} kpi={kpi}
                    format={format} accent={KPI_ACCENTS[widgetId]}
                    lowerIsBetter={lowerIsBetter}
                    hideComparisons={KPI_NO_COMPARISONS.has(widgetId)}
                    showLastWeek={false}
                    showYesterday={false}
                    showLastMonth={hasCompare}
                    showLastYear={false}
                    showPctChange={settings.showPctChange}
                    showNetChange={settings.showNetChange}
                    primaryComparison={hasCompare ? 'lastMonth' : 'none'}
                    comparisonLabel={hasCompare ? compLabel : undefined}
                    description={description}
                    adminQueries={queries}
                />
            )
        }

        switch (widgetId) {
            case 'performanceChart':
                return data.timeSeries && data.granularity
                    ? <PerformanceChart
                        data={data.timeSeries}
                        compareData={data.compareTimeSeries}
                        granularity={data.granularity}
                        includeReturns={includeReturns}
                        comparisonLabel={hasCompare ? compLabel : undefined}
                        startDate={startDate}
                      />
                    : null
            case 'channelMix':
                return data.channelMix ? <ChannelMixBar mix={data.channelMix} /> : null
            case 'hourlyChart':
                return <HourlyChart hourly={data.hourly ?? []} settings={settings} channel={channel} />
            case 'paymentMix':
                return <PaymentDonut mix={data.paymentMix} />
            case 'topSellers': {
                const presets = buildPresets()
                const activePreset = activePresetId ? presets.find(p => p.id === activePresetId) : null
                const customTitle = activePreset ? `Top Sellers ${activePreset.label}` : `Top Sellers ${startDate} – ${endDate}`
                return <TopSellersPanel sellers={data.topSellers} channel={channel} title={customTitle} />
            }
            case 'soldOut':
                return <SoldOutPanel items={data.soldOutItems} />
            case 'salesByStore':
                return <SalesByStoreChart data={salesByStoreData || []} settings={settings} selectedStoreId={storeId} />
            case 'employeePerformance': {
                const activePreset = activePresetId ? buildPresets().find(p => p.id === activePresetId) : null
                return (
                    <EmployeePerformancePanel
                        data={employeePerformanceData}
                        settings={settings}
                        periodLabel={activePreset ? activePreset.label : fmtRangeLabel(startDate, endDate)}
                        channel={channel}
                        includeReturns={includeReturns}
                        selectedStoreId={storeId}
                        isLoading={employeePerformancePending}
                        error={employeePerformanceError}
                        query={employeePerformanceQuery}
                        storeLabel={storeSelectionLabel(storeId, storeConfig?.stores)}
                        isEditing={isEditing}
                        isStale={employeePerformanceStale}
                    />
                )
            }
            default:
                return <div style={{ padding: '1rem', color: 'var(--color-text-tertiary)' }}>Unknown widget: {widgetId}</div>
        }
    }

    if (configLoading) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1rem 0' }}>
                <div className="page-header"><h1>{greeting}, {user?.displayName.split(' ')[0]} 👋</h1></div>
                <div className="dashboard-loading"><div className="spinner" /><span>Loading configuration…</span></div>
            </div>
        )
    }

    if (user?.role === 'Unassigned') return <Navigate to="/my-stores" replace />
    if (storeConfig && storeConfig.stores.length === 0 && !storeConfig.canViewAll) return <Navigate to="/my-stores" replace />

    return (
        <>
            <div className="page-header">
                <h1>{greeting}, {user?.displayName.split(' ')[0]} 👋</h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                    <select
                        className="header-store-select"
                        value={storeId}
                        onChange={e => { setStoreId(e.target.value); savePreference(e.target.value, channel) }}
                    >
                        {storeConfig?.stores.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                        {storeConfig?.hasGroup && <option value="group">My Store Group</option>}
                        {storeConfig?.canViewAll && <option value="all">All Stores</option>}
                    </select>

                    <div className="desktop-date-selector">
                        <DateRangePicker
                            startDate={startDate}
                            endDate={endDate}
                            activePresetId={activePresetId}
                            onApply={handleRangeApply}
                        />
                    </div>
                    
                    <div className="mobile-date-range-display">
                        {mobileDateLabel}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap' }}>
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', whiteSpace: 'nowrap' }}>
                            Compare to:
                        </span>
                        <select
                            value={compareMode}
                            onChange={e => {
                                setCompareMode(e.target.value)
                                setDateSettings(activePresetId, e.target.value, customCompareStart, customCompareEnd)
                            }}
                            className="header-store-select"
                        >
                            {compareOptions.map(o => (
                                <option key={o.id} value={o.id}>{o.label}</option>
                            ))}
                        </select>

                        {/* Inline date inputs for custom comparison */}
                        {compareMode === 'custom' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, animation: 'fadeIn 0.2s ease' }}>
                                <input
                                    type="date"
                                    value={customCompareStart}
                                    onChange={e => {
                                        setCustomCompareStart(e.target.value)
                                        setDateSettings(activePresetId, compareMode, e.target.value, customCompareEnd)
                                    }}
                                    style={{
                                        padding: '3px 6px', borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--color-border)', background: 'var(--color-bg)',
                                        color: 'var(--color-text)', fontSize: 'var(--text-xs)',
                                        fontFamily: 'inherit', colorScheme: 'dark', maxWidth: '130px'
                                    }}
                                />
                                <span style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--text-xs)' }}>→</span>
                                <input
                                    type="date"
                                    value={customCompareEnd}
                                    onChange={e => {
                                        setCustomCompareEnd(e.target.value)
                                        setDateSettings(activePresetId, compareMode, customCompareStart, e.target.value)
                                    }}
                                    style={{
                                        padding: '3px 6px', borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--color-border)', background: 'var(--color-bg)',
                                        color: 'var(--color-text)', fontSize: 'var(--text-xs)',
                                        fontFamily: 'inherit', colorScheme: 'dark', maxWidth: '130px'
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Toolbar ────────────────────────────────────────────── */}
            <div className="dashboard-toolbar">

                <div className="seg-control">
                    {(['all', 'instore', 'online'] as const).map(ch => (
                        <button key={ch} className={channel === ch ? 'active' : ''}
                            onClick={() => { setChannel(ch); savePreference(storeId, ch) }}>
                            {ch === 'all' ? 'ALL' : ch === 'instore' ? 'IN-STORE' : 'ONLINE'}
                        </button>
                    ))}
                </div>

                <div className="seg-control">
                    <button className={includeReturns ? 'active' : ''} onClick={() => setIncludeReturns(true)}>NET</button>
                    <button className={!includeReturns ? 'active' : ''} onClick={() => setIncludeReturns(false)}>GROSS</button>
                </div>


                <label className="toolbar-checkbox">
                    <input
                        type="checkbox"
                        checked={includeMultibuys}
                        onChange={(e) => setIncludeMultibuys(e.target.checked)}
                    />
                    Include Multibuys
                </label>

                <label className="toolbar-checkbox">
                    <input
                        type="checkbox"
                        checked={compareFull}
                        onChange={(e) => setCompareFull(e.target.checked)}
                    />
                    Compare Full
                </label>

                {!isEditing ? (
                    <div className="desktop-actions-menu-container" style={{ position: 'relative', marginLeft: 'auto' }}>
                        <button className="toolbar-refresh-btn" onClick={() => setActionMenuOpen(x => !x)} title="Dashboard Actions">
                            ⚙ Actions
                        </button>
                        {actionMenuOpen && (
                            <>
                                <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setActionMenuOpen(false)} />
                                <div className="dashboard-actions-menu">
                                    {dataUpdatedAt > 0 && (
                                        <div style={{ padding: '8px 12px', fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', borderBottom: '1px solid var(--color-border)', marginBottom: '4px' }}>
                                            Updated {new Date(dataUpdatedAt).toLocaleTimeString()}
                                        </div>
                                    )}
                                    <button className="nav-item" onClick={() => { refresh(); setActionMenuOpen(false); }} style={{ padding: '8px 12px' }}>
                                        <span className="nav-icon">↻</span>
                                        <span className="nav-label">Refresh</span>
                                    </button>
                                    <button className="nav-item" onClick={() => { setEditing(true); setActionMenuOpen(false); }} style={{ padding: '8px 12px' }}>
                                        <span className="nav-icon">✏️</span>
                                        <span className="nav-label">Edit Layout</span>
                                    </button>
                                    {hasDiagnostics && (
                                        <button className="nav-item" onClick={() => { setShowDebug(true); setActionMenuOpen(false); }} style={{ padding: '8px 12px', color: 'var(--color-text-tertiary)' }}>
                                            <span className="nav-icon">🐛</span>
                                            <span className="nav-label">Debug SQL</span>
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginLeft: 'auto' }}>
                        <button className="edit-mode-btn" onClick={() => setShowGallery(true)}>
                            ➕ Add Widget
                        </button>
                        <LayoutPresetsMenu
                            {...layoutUi.menuProps}
                            onExport={handleExportLayout}
                            onImport={handleImportLayout}
                        />
                        <button className="edit-mode-btn cancel" onClick={handleCancel}>
                            ✕ Cancel
                        </button>
                        <button className="edit-mode-btn" onClick={layoutUi.openSaveDialog}>
                            Save as…
                        </button>
                        <button className="edit-mode-btn done" onClick={layoutUi.quickSave} disabled={layoutUi.busy}>
                            {layoutUi.busy ? 'Saving…' : '💾 Save'}
                        </button>
                    </div>
                )}
            </div>

            {/* ── Debug Modal ───────────────────────────────────────── */}
            {showDebug && <SqlDebugModal queries={data?.debug ?? []} activeProfile={data?.activeProfile} connectionInfo={data?.connectionInfo} onClose={() => setShowDebug(false)} />}

            {/* ── Widget Gallery ────────────────────────────────────── */}
            {showGallery && <WidgetGallery onClose={() => setShowGallery(false)} dashboardType="custom-range" />}

            {/* ── Save Layout dialog, Revert confirmation ─────────────── */}
            {layoutUi.dialogs}

            {/* ── Content ────────────────────────────────────────────── */}
            {layoutUi.banner}
            {isLoading && (
                <div className="dashboard-loading"><div className="spinner" /><span>Loading dashboard…</span></div>
            )}
            {error && <div className="dashboard-error">Failed to load dashboard data. Please try again.</div>}
            {data && !data.success && <div className="dashboard-error">{data.errorMessage}</div>}

            {data?.success && (
                <MonthlyWidgetGrid
                    layouts={layouts}
                    activeWidgets={activeWidgets}
                    widgetSettings={widgetSettings}
                    isEditing={isEditing}
                    layoutVersion={layoutVersion}
                    setLayouts={setLayouts}
                    snapshotLayouts={snapshotLayouts}
                    removeWidget={removeWidget}
                    updateWidgetSettings={updateWidgetSettings}
                    renderWidget={renderWidget}
                    settingsOpen={settingsOpen}
                    setSettingsOpen={setSettingsOpen}
                    isFetching={isFetching || salesByStoreFetching || employeePerformanceFetching}
                />
            )}

            <MobileFooter
                channel={channel}
                setChannel={(ch) => { setChannel(ch); savePreference(storeId, ch); }}
                includeReturns={includeReturns}
                setIncludeReturns={setIncludeReturns}
                includeMultibuys={includeMultibuys}
                setIncludeMultibuys={setIncludeMultibuys}
                compareFull={compareFull}
                setCompareFull={setCompareFull}
                onActionClick={() => setActionMenuOpen(x => !x)}
                customDateSelector={
                    <DateRangePicker
                        startDate={startDate}
                        endDate={endDate}
                        activePresetId={activePresetId}
                        onApply={handleRangeApply}
                        iconOnly={true}
                    />
                }
            />

            {/* Mobile Actions Menu */}
            {actionMenuOpen && (
                <div className="mobile-actions-menu-wrapper">
                    <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setActionMenuOpen(false)} />
                    <div className="mobile-actions-menu">
                        {!isEditing ? (
                            <>
                                {dataUpdatedAt > 0 && (
                                    <div style={{ padding: '8px 12px', fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', borderBottom: '1px solid var(--color-border)', marginBottom: '4px' }}>
                                        Updated {new Date(dataUpdatedAt).toLocaleTimeString()}
                                    </div>
                                )}
                                <button className="nav-item" onClick={() => { refresh(); setActionMenuOpen(false); }} style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: 'var(--color-text)', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                    <span className="nav-icon">↻</span>
                                    <span className="nav-label">Refresh</span>
                                </button>
                                <button className="nav-item" onClick={() => { setEditing(true); setActionMenuOpen(false); }} style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: 'var(--color-text)', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                    <span className="nav-icon">✏️</span>
                                    <span className="nav-label">Edit Layout</span>
                                </button>
                                {hasDiagnostics && (
                                    <button className="nav-item" onClick={() => { setShowDebug(true); setActionMenuOpen(false); }} style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: 'var(--color-text-tertiary)', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                        <span className="nav-icon">🐛</span>
                                        <span className="nav-label">Debug SQL</span>
                                    </button>
                                )}
                            </>
                        ) : (
                            <>
                                <button className="nav-item" onClick={() => { setShowGallery(true); setActionMenuOpen(false); }} style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: 'var(--color-text)', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                    <span className="nav-icon">➕</span>
                                    <span className="nav-label">Add Widget</span>
                                </button>
                                {layoutUi.hasMyLayout && (
                                    <button className="nav-item" onClick={() => { layoutUi.requestRevert(); setActionMenuOpen(false); }} style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: 'var(--color-error)', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                        <span className="nav-icon">↺</span>
                                        <span className="nav-label">Revert to System Default</span>
                                    </button>
                                )}
                                <button className="nav-item" onClick={() => { handleCancel(); setActionMenuOpen(false); }} style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: 'var(--color-text)', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                    <span className="nav-icon">✕</span>
                                    <span className="nav-label">Cancel Edits</span>
                                </button>
                                <button className="nav-item" onClick={() => { layoutUi.openSaveDialog(); setActionMenuOpen(false); }} style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: 'var(--color-text)', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                    <span className="nav-icon">🗂</span>
                                    <span className="nav-label">Save As…</span>
                                </button>
                                <button className="nav-item" onClick={() => { layoutUi.quickSave(); setActionMenuOpen(false); }} style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: 'var(--color-primary)', width: '100%', textAlign: 'left', cursor: 'pointer', fontWeight: 'bold' }}>
                                    <span className="nav-icon">💾</span>
                                    <span className="nav-label">Save Edits</span>
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    )
}
